import { LockOutlined } from "@ant-design/icons"
import { Flex } from "antd"
import { useTranslation } from "react-i18next"

import { tLiteral } from "../../../utils/questionText"
import InlineChipInput from "../chips/InlineChipInput"
import RemovableChip from "../chips/RemovableChip"

import chipStyles from "../../chips.module.less"

// Statement descriptors pulled (and locked) from the merchant's connected processors,
// primary first. Read from building_blocks.processors[<slug>].
const verifiedProcessorDescriptors = (buildingBlocks) => {
  const procs = buildingBlocks?.processors
  if (!procs || typeof procs !== "object") return []
  const connected = Array.isArray(procs.connected) ? procs.connected : []
  const out = []
  for (const slug of connected) {
    const d = procs[slug] && procs[slug].statement_descriptor
    if (d && !out.includes(d)) out.push(d)
  }
  return out
}

// Derive a clean domain (name + TLD, no protocol / www / path / port) from a URL, e.g.
// "https://www.dropship.io/about" -> "dropship.io". Seeds the billing-descriptor
// placeholder from the website the merchant entered in required_information.
const domainFromUrl = (url) => {
  let d = String(url || "")
    .trim()
    .toLowerCase()
  d = d.replace(/^https?:\/\//, "").replace(/^www\./, "")
  d = d.split(/[/?#]/)[0].replace(/:\d+$/, "")
  return d
}

// Multi-value free text (billing descriptors): each value is a removable chip, "+ Add"
// appends another. Stores a string[]; tolerates a legacy single string.
const TextMultiControl = ({ question, value, onChange, onTouch, buildingBlocks }) => {
  const { t } = useTranslation()

  let values = Array.isArray(value) ? value : value ? [String(value)] : []
  // Verified descriptors render as locked, non-removable chips; the merchant's own
  // aliases stay editable below them.
  const lockedValues =
    question.verifiedFrom === "processors" ? verifiedProcessorDescriptors(buildingBlocks) : []
  if (lockedValues.length) {
    const lower = new Set(lockedValues.map((v) => String(v).toLowerCase()))
    values = values.filter((v) => !lower.has(String(v).toLowerCase()))
  }

  // `domainPlaceholder` seeds the example from the merchant's step-1 website URL (name +
  // TLD, upper-cased like a statement descriptor), falling back to SPACEX.COM.
  const placeholder = question.domainPlaceholder
    ? t("e.g. _domain_", {
        domain: (
          domainFromUrl(buildingBlocks?.required_information?.website_url) || "spacex.com"
        ).toUpperCase(),
        nsSeparator: false,
        keySeparator: false,
        defaultValue: "e.g. _domain_"
      })
    : tLiteral(t, question.placeholder)

  const commit = (v) => {
    if (values.some((x) => String(x).toLowerCase() === v.toLowerCase())) return
    onTouch()
    onChange([...values, v])
  }

  return (
    <Flex wrap="wrap" gap={8} className={chipStyles.chipRow}>
      {lockedValues.map((c, i) => (
        <span
          key={`lk-${i}`}
          className={`${chipStyles.chip} ${chipStyles.chipCustom} ${chipStyles.chipOn} ${chipStyles.chipVerified}`}
          title={t(
            "Verified from your connected processor. This is what prints on the cardholder statement."
          )}
        >
          <LockOutlined className={chipStyles.chipVerifiedIcon} />
          <span className={chipStyles.chipCustomText}>{c}</span>
        </span>
      ))}
      {values.map((c, i) => (
        <RemovableChip
          key={`tm-${i}`}
          label={c}
          onRemove={() => {
            onTouch()
            onChange(values.filter((_, idx) => idx !== i))
          }}
        />
      ))}
      <InlineChipInput
        minWidth={140}
        emptyChars={placeholder?.length || 12}
        placeholder={placeholder}
        addLabel={
          values.length || lockedValues.length
            ? t("+ Add another")
            : t(question.addLabel || "+ Add")
        }
        onCommit={commit}
      />
    </Flex>
  )
}

export default TextMultiControl
